import moment from "moment";
import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    Check as ApproveIcon,
    Close as RejectIcon,
    Visibility as ViewIcon,
    Refresh as RefreshIcon,
} from "@mui/icons-material";
import { api_urls } from "../../../utils/api-urls";

const WithdrawalRequest = () => {
    const [withdrawalRequests, setWithdrawalRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchWithdrawalRequests();
    }, []);

    const fetchWithdrawalRequests = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${api_urls}api/admin/get_withdrawal_requests`);
            const data = await response.json();
            if (data.success) {
                setWithdrawalRequests(data.withdrawalRequests || []);
            }
        } catch (error) {
            console.error("Error fetching withdrawal requests:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setRemarks(request.remarks || "");
        setDialogOpen(true);
    };

    const handleUpdateStatus = async (status) => {
        if (!selectedRequest) return;
        
        setActionLoading(true);
        try {
            const response = await fetch(`${api_urls}api/admin/update_withdrawal_status`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    requestId: selectedRequest._id,
                    status: status,
                    remarks: remarks
                }),
            });
            const data = await response.json();
            if (data.success) {
                alert(`Withdrawal request ${status.toLowerCase()} successfully!`);
                setDialogOpen(false);
                fetchWithdrawalRequests();
            } else {
                alert(data.message || "Failed to update status");
            }
        } catch (error) {
            console.error("Error updating status:", error);
            alert("Error updating status");
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Approved":
            case "Completed":
                return "success";
            case "Rejected":
                return "error";
            case "Processing":
                return "info";
            default:
                return "warning";
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    Withdrawal Requests
                </Typography>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchWithdrawalRequests} color="primary">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell>S.No.</TableCell>
                                <TableCell>Astrologer Name</TableCell>
                                <TableCell>Mobile</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Request Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {withdrawalRequests.map((request, index) => (
                                <TableRow key={request._id} hover>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <Typography fontWeight="bold">
                                            {request.astrologerName || "Unknown"}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {request.astrologerEmail || ""}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{request.astrologerPhone || "N/A"}</TableCell>
                                    <TableCell>
                                        <Typography fontWeight="bold" color="primary">
                                            ₹{request.amount?.toLocaleString() || 0}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {moment(request.createdAt).format("DD MMM YYYY, hh:mm A")}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={request.status || "Pending"}
                                            color={getStatusColor(request.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewDetails(request)}
                                                color="info"
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {(!request.status || request.status === "Pending") && (
                                            <>
                                                <Tooltip title="Approve">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => {
                                                            setSelectedRequest(request);
                                                            handleUpdateStatus("Approved");
                                                        }}
                                                    >
                                                        <ApproveIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reject">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedRequest(request);
                                                            handleUpdateStatus("Rejected");
                                                        }}
                                                    >
                                                        <RejectIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {withdrawalRequests.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography color="textSecondary" py={3}>
                                            No withdrawal requests found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Details Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: "#f5f5f5" }}>
                    Withdrawal Request Details
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedRequest && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Astrologer</Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {selectedRequest.astrologerName || "Unknown"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Contact</Typography>
                                <Typography variant="body1">
                                    {selectedRequest.astrologerPhone || "N/A"} | {selectedRequest.astrologerEmail || "N/A"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Withdrawal Amount</Typography>
                                <Typography variant="h5" color="primary" fontWeight="bold">
                                    ₹{selectedRequest.amount?.toLocaleString() || 0}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Request Date</Typography>
                                <Typography variant="body1">
                                    {moment(selectedRequest.createdAt).format("DD MMMM YYYY, hh:mm A")}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                                <Chip
                                    label={selectedRequest.status || "Pending"}
                                    color={getStatusColor(selectedRequest.status)}
                                    size="small"
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Bank Details</Typography>
                                <Typography variant="body1">
                                    {selectedRequest.bankName || "N/A"} - {selectedRequest.accountNumber || "N/A"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    IFSC: {selectedRequest.ifscCode || "N/A"}
                                </Typography>
                            </Box>
                            <TextField
                                label="Remarks (Optional)"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                multiline
                                rows={2}
                                fullWidth
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} variant="outlined">
                        Close
                    </Button>
                    {selectedRequest && (!selectedRequest.status || selectedRequest.status === "Pending") && (
                        <>
                            <Button
                                onClick={() => handleUpdateStatus("Rejected")}
                                variant="contained"
                                color="error"
                                startIcon={<RejectIcon />}
                                disabled={actionLoading}
                            >
                                Reject
                            </Button>
                            <Button
                                onClick={() => handleUpdateStatus("Approved")}
                                variant="contained"
                                color="success"
                                startIcon={<ApproveIcon />}
                                disabled={actionLoading}
                            >
                                Approve
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WithdrawalRequest;
